'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:synergiefrontends":true});}catch(e){}}();

import React, { useEffect, useState } from 'react';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, Alert } from 'reactstrap';
import queryString from 'query-string';
import { useParams, useSearchParams } from 'next/navigation';
import useData from '../hooks/useData';
import axiosInstance from '../utils/axios';
import useNotifications from '../hooks/useNotifications';
import { getI18nUtils } from '../i18n/i18n-utils';
import { convertSearchParamsToJobSearchQuery, getActiveFacetsFromSearchParams } from '../api/jobs';
import { Locale } from '../i18n/i18n-config';

interface ModalSavedSearch extends SavedSearch {
  overwrite: string;
}

const defaultSavedSearch: ModalSavedSearch = {
  name: '',
  notifications: {
    email: false,
    push: false,
  },
  overwrite: '',
};

const JobSearchSaveModal = ({
  isOpen = true,
  toggle,
  setSaveSuccess,
  dictionary,
}: {
  isOpen?: boolean;
  toggle: () => void;
  setSaveSuccess: React.Dispatch<React.SetStateAction<boolean>>;
  dictionary: Record<string, string>;
}): JSX.Element => {
  const searchParams = useSearchParams();
  const { locale } = useParams<{ locale: Locale }>();
  const { formatMessage } = getI18nUtils(dictionary);
  const { hasPermission, askPermission } = useNotifications();
  const { data: savedSearches, refetchData } = useData<SavedSearch[]>('/searches');
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState<{ message: string; color: AlertColor } | null>(null);
  const [savedSearch, setSavedSearch] = useState<ModalSavedSearch>(defaultSavedSearch);

  const { name, notifications, overwrite } = savedSearch;
  const { email, push } = notifications;

  const activeFilters = convertSearchParamsToJobSearchQuery(searchParams, locale, { exclude: ['page'] });

  const handleSubmitError = (msg) => {
    if (msg && msg.indexOf('ERR_DUP_NAME') !== -1) setAlert({ message: 'save_jobsearch_duplicate', color: 'warning' });
    else if (msg && msg.indexOf('ERR_UPDATE_SUPPR') !== -1)
      setAlert({ message: 'save_jobsearch_handle_suppressions', color: 'warning' });
    else setAlert({ message: 'job-detail-form-default-errorMessage', color: 'danger' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setAlert(null);

    if (!name) {
      setAlert({ message: 'save_jobsearch_choose_title', color: 'warning' });
      setSubmitting(false);
      return;
    }

    const data: any = {
      name,
      notifications: { email, push },
      query: { ...activeFilters, language: locale },
    };

    if (data.query.radius) data.query.radius = parseInt(data.query.radius);
    if (data.query.what) {
      data.query.search = data.query.what;
      delete data.query.what;
    }

    data.url = `https://www.${process.env.NEXT_PUBLIC_APP}/${locale}/jobs/?${queryString.stringify(activeFilters, { skipNull: true, skipEmptyString: true })}`;
    data.query.app = process.env.NEXT_PUBLIC_APP;

    const method = overwrite ? 'PUT' : 'POST';

    try {
      await axiosInstance(`/searches/${overwrite}`, { method, data });

      setSavedSearch(defaultSavedSearch);
      refetchData();
      setSaveSuccess(true);
      toggle();
    } catch (error) {
      handleSubmitError(error.response.data.message);
    }
    setSubmitting(false);
  };

  useEffect(() => {
    if (savedSearches && savedSearches.length > 9) setSavedSearch({ ...savedSearch, overwrite: savedSearches[0]._id || '' });
  }, [savedSearches]);

  useEffect(() => {
    const getPushPermissionAsync = async () => {
      if (push && !hasPermission()) {
        try {
          // Push checkbox was enabled, but no permission granted (yet)
          await askPermission();
        } catch {
          // User denied permission: set checkbox back to off & show alert
          setAlert({ message: 'push_notifications_blocked', color: 'warning' });
          setSavedSearch({ ...savedSearch, notifications: { email, push: false } });
        }
      }
    };
    getPushPermissionAsync();
  }, [push]);

  const activeFacets = getActiveFacetsFromSearchParams(searchParams, { exclude: ['sort', 'page'] });
  const noActiveFilters = Object.values(activeFacets).flat().length === 0;

  return (
    <Modal isOpen={isOpen} toggle={toggle}>
      <Form onSubmit={handleSubmit}>
        <ModalHeader toggle={toggle}>{formatMessage({ id: 'save_jobsearch_modal_title' })}</ModalHeader>
        <ModalBody>
          {noActiveFilters ? (
            <div className="my-3">{formatMessage({ id: 'save_jobsearch_no_filters' })}</div>
          ) : (
            <>
              {alert && <Alert color={alert.color}>{formatMessage({ id: alert.message })}</Alert>}

              {savedSearches && savedSearches.length > 9 && overwrite && (
                <>
                  <div className="mb-3">{formatMessage({ id: 'save_jobsearch_max_saved_searches' })}</div>
                  <FormGroup>
                    <Label>{formatMessage({ id: 'save_jobsearch_choose_overwrite' })}</Label>
                    <Input
                      type="select"
                      name="select"
                      id="select"
                      onChange={(e) =>
                        setSavedSearch({
                          ...savedSearch,
                          overwrite: savedSearches.find((search) => search.name === e.target.value)?._id || '',
                        })
                      }
                    >
                      {savedSearches.map((search) => (
                        <option key={search._id}>{search.name}</option>
                      ))}
                    </Input>
                  </FormGroup>
                </>
              )}
              <FormGroup>
                <Label>{formatMessage({ id: 'save_jobsearch_choose_title' })}</Label>
                <Input
                  id="jobsearchTitle"
                  name="jobsearchTitle"
                  type="text"
                  invalid={Boolean(alert && !savedSearch.name)}
                  value={savedSearch.name}
                  onChange={({ target }) => setSavedSearch({ ...savedSearch, name: target.value })}
                />
              </FormGroup>
              <FormGroup check className="d-flex align-items-start">
                <Input
                  type="checkbox"
                  id="emailNotifications"
                  name="emailNotifications"
                  onChange={() =>
                    setSavedSearch({
                      ...savedSearch,
                      notifications: { ...notifications, email: !notifications.email },
                    })
                  }
                  checked={savedSearch.notifications.email}
                />
                <Label for="emailNotifications" check>
                  {formatMessage({ id: 'save_jobsearch_choose_email_notifications' })}
                </Label>
              </FormGroup>
              <FormGroup check className="d-flex align-items-start">
                <Input
                  type="checkbox"
                  id="pushNotifications"
                  name="pushNotifications"
                  onChange={() =>
                    setSavedSearch({
                      ...savedSearch,
                      notifications: { ...notifications, push: !notifications.push },
                    })
                  }
                  checked={savedSearch.notifications.push}
                />
                <Label for="pushNotifications" check>
                  {formatMessage({ id: 'save_jobsearch_choose_push_notifications' })}
                </Label>
              </FormGroup>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          {noActiveFilters ? (
            <Button color="primary" onClick={toggle}>
              Ok
            </Button>
          ) : (
            <>
              <Button type="submit" color="primary" disabled={submitting}>
                {formatMessage({ id: 'save_jobsearch_save' })}
              </Button>
              <Button type="button" color="secondary" outline onClick={toggle} disabled={submitting}>
                {formatMessage({ id: 'cancel' })}
              </Button>
            </>
          )}
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default JobSearchSaveModal;
